/** Versioned live-chart contract. Keep identical to the CMS liveChartContract.ts. */
export interface LiveChartProjection {
  version: 1;
  groupBy:
    | "scope"
    | "category"
    | "subcategory"
    | "series"
    | "total"
    | "ranked"
    | "year"
    | "hierarchy";
  series?: string[];
  limit?: number;
  trend?: {
    method: "same" | "moving_average" | "linear_regression";
    type: "line" | "area" | "bar";
    period: number;
  };
}

const groups: Record<string, string[]> = {
  allemissions: ["scope", "category", "subcategory", "hierarchy"],
  allemissionsbymonth: ["series", "total"],
  emissionsbyscopeandcategories: ["series", "total"],
  emissionsbyunit: ["ranked"],
  emissionsbyactivity: ["ranked"],
  emissionsbyyear: ["year"],
};
const numericCharts = [
  "line",
  "area",
  "bar",
  "pie",
  "donut",
  "radar",
  "polarArea",
  "treemap",
];
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const only = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).every((k) => keys.includes(k));

export function liveProjection(
  source: unknown,
  chartType: unknown,
  value: unknown,
): LiveChartProjection | null {
  if (
    typeof source !== "string" ||
    typeof chartType !== "string" ||
    !record(value)
  )
    return null;
  if (
    !only(value, ["version", "groupBy", "series", "limit", "trend"]) ||
    value.version !== 1 ||
    typeof value.groupBy !== "string" ||
    !groups[source]?.includes(value.groupBy)
  )
    return null;
  if (value.groupBy === "hierarchy") {
    if (chartType !== "sankey" || Object.keys(value).length !== 2) return null;
  } else if (!numericCharts.includes(chartType)) return null;
  if (
    value.series !== undefined &&
    (!["series", "total"].includes(value.groupBy) ||
      !Array.isArray(value.series) ||
      !value.series.length ||
      value.series.length > 1000 ||
      !value.series.every(
        (s) => typeof s === "string" && s.trim().length > 0,
      ) ||
      new Set(value.series).size !== value.series.length)
  )
    return null;
  if (
    value.limit !== undefined &&
    (value.groupBy !== "ranked" ||
      !Number.isInteger(value.limit) ||
      (value.limit as number) < 1 ||
      (value.limit as number) > 1000)
  )
    return null;
  if (
    ["pie", "donut", "polarArea", "treemap"].includes(chartType) &&
    value.groupBy === "series" &&
    (!Array.isArray(value.series) || value.series.length !== 1)
  )
    return null;
  if (value.trend !== undefined) {
    const t = value.trend;
    if (
      !["line", "area", "bar"].includes(chartType) ||
      !["series", "total", "year"].includes(value.groupBy) ||
      !record(t) ||
      !only(t, ["method", "type", "period"]) ||
      !["same", "moving_average", "linear_regression"].includes(
        String(t.method),
      ) ||
      !["line", "area", "bar"].includes(String(t.type)) ||
      !Number.isInteger(t.period) ||
      (t.period as number) < 1 ||
      (t.period as number) > 120
    )
      return null;
  }
  return value as unknown as LiveChartProjection;
}

/** Missing legacy descriptors cannot safely recover an arbitrary chat transformation. */
export function getLiveProjection(spec: unknown): LiveChartProjection | null {
  if (!record(spec) || !record(spec.dataSource)) return null;
  const source = spec.dataSource;
  if (
    !only(source, ["source", "params", "projection"]) ||
    (source.params !== undefined &&
      (!record(source.params) || Object.keys(source.params).length))
  )
    return null;
  return liveProjection(source.source, spec.chartType, source.projection);
}

/** Check that the saved shape can be reproduced by the descriptor and renderer. */
export function isSavableLiveChart(spec: unknown): boolean {
  const p = getLiveProjection(spec);
  if (!p || !record(spec) || !record(spec.data)) return false;
  const data = spec.data;
  if (
    data.secondaryAxis !== undefined ||
    (data.unit !== undefined &&
      !["kgCO2e", "tCO2e"].includes(String(data.unit)))
  )
    return false;
  if (p.groupBy === "hierarchy")
    return (
      Array.isArray(data.links) &&
      data.links.length > 0 &&
      data.links.every(
        (link) =>
          record(link) &&
          typeof link.source === "string" &&
          typeof link.target === "string" &&
          typeof link.value === "number" &&
          Number.isFinite(link.value) &&
          link.value >= 0,
      )
    );
  if (
    !Array.isArray(data.labels) ||
    !data.labels.length ||
    !data.labels.every((label) => typeof label === "string") ||
    !Array.isArray(data.datasets) ||
    !data.datasets.length
  )
    return false;
  const rows = data.datasets;
  const base = p.trend ? rows.slice(0, -1) : rows;
  if (!base.length || (p.groupBy !== "series" && base.length !== 1))
    return false;
  if (
    p.groupBy === "series" &&
    p.series &&
    (base.length !== p.series.length ||
      base.some((row, i) => !record(row) || row.label !== p.series![i]))
  )
    return false;
  if (p.trend) {
    const overlay = rows[rows.length - 1];
    if (
      !record(overlay) ||
      overlay.label !== trendLabel(p.trend) ||
      overlay.type !== p.trend.type
    )
      return false;
  }
  return rows.every(
    (row) =>
      record(row) &&
      typeof row.label === "string" &&
      (row.type === undefined ||
        ["line", "area", "bar"].includes(String(row.type))) &&
      Array.isArray(row.values) &&
      row.values.length === (data.labels as unknown[]).length &&
      row.values.every((v, i) =>
        spec.chartType === "treemap"
          ? record(v) &&
            v.x === (data.labels as unknown[])[i] &&
            typeof v.value === "number" &&
            Number.isFinite(v.value) &&
            v.value >= 0
          : typeof v === "number" && Number.isFinite(v),
      ),
  );
}

export function trendValues(
  values: number[],
  method: string,
  period: number,
): number[] {
  if (method === "moving_average")
    return values.map((_, i) => {
      const start = Math.max(0, i - Math.floor(period / 2));
      const slice = values.slice(
        start,
        Math.min(values.length, start + period),
      );
      return slice.reduce((a, b) => a + b, 0) / slice.length;
    });
  if (method === "linear_regression") {
    const xMean = (values.length - 1) / 2;
    const yMean = values.reduce((a, b) => a + b, 0) / values.length;
    let num = 0,
      den = 0;
    values.forEach((v, i) => {
      num += (i - xMean) * (v - yMean);
      den += (i - xMean) ** 2;
    });
    return values.map((_, i) => yMean + (den ? num / den : 0) * (i - xMean));
  }
  return [...values];
}

export function trendLabel(
  trend: NonNullable<LiveChartProjection["trend"]>,
): string {
  return trend.method === "moving_average"
    ? `Moving Average (${trend.period}-period)`
    : trend.method === "linear_regression"
      ? "Linear Regression"
      : "Trend";
}
